import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { ExternalLink, Loader2 } from 'lucide-react';
import { useAuth } from '@/hooks/useAuthApi';
import { toast } from '@/hooks/use-toast';
import { queryKeys } from '@/queries/keys';
import slackService, { SlackConfig, UpdateSlackNotifications } from '@/lib/api/slack';

interface SlackConfigDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organizationId: string;
}

const NOTIFICATION_TOGGLES: {
  key: keyof UpdateSlackNotifications;
  label: string;
  description: string;
}[] = [
  { key: 'notify_on_qualified', label: 'Qualified leads', description: 'When a visitor is identified as a qualified lead' },
  { key: 'notify_on_booked', label: 'Meetings booked', description: 'When a visitor books a meeting' },
  { key: 'notify_on_active_session', label: 'Active sessions', description: 'When a visitor starts interacting with your agent' },
  { key: 'notify_on_high_intent', label: 'High intent signals', description: 'When a visitor shows strong buying intent' },
  { key: 'notify_on_all_calls', label: 'All calls', description: 'Notify on every conversation (can be noisy)' },
];

const SlackConfigDialog: React.FC<SlackConfigDialogProps> = ({
  open,
  onOpenChange,
  organizationId,
}) => {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  const configQuery = useQuery({
    queryKey: queryKeys.slack.config(organizationId),
    enabled: open && !!organizationId,
    queryFn: async () => {
      const response = await slackService.getConfig(organizationId);
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to load Slack configuration.');
      }
      return response.data;
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (data: UpdateSlackNotifications) => {
      const response = await slackService.updateNotifications(organizationId, data);
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to update notification settings.');
      }
      return response.data;
    },
    onMutate: async (data) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.slack.config(organizationId) });
      const previous = queryClient.getQueryData<SlackConfig>(queryKeys.slack.config(organizationId));
      if (previous) {
        queryClient.setQueryData<SlackConfig>(queryKeys.slack.config(organizationId), {
          ...previous,
          ...data,
        });
      }
      return { previous };
    },
    onError: (_err, _data, context) => {
      if (context?.previous) {
        queryClient.setQueryData(queryKeys.slack.config(organizationId), context.previous);
      }
      toast({
        title: 'Update failed',
        description: 'Could not save notification settings. Please try again.',
        variant: 'destructive',
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.slack.config(organizationId) });
    },
  });

  const config = configQuery.data;

  const handleToggle = (key: keyof UpdateSlackNotifications, currentValue: boolean) => {
    updateMutation.mutate({ [key]: !currentValue });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Slack Notifications</DialogTitle>
          <DialogDescription>
            Choose which events send notifications to your Slack channel.
          </DialogDescription>
        </DialogHeader>

        {configQuery.isLoading ? (
          <div className="flex items-center justify-center py-8 text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading configuration...
          </div>
        ) : configQuery.isError ? (
          <div className="py-8 text-center text-sm text-destructive">
            Failed to load Slack configuration.
          </div>
        ) : !config?.connected ? (
          <div className="py-6 text-center">
            <p className="text-sm text-muted-foreground">Slack is not connected yet.</p>
            <p className="text-sm text-muted-foreground mt-1">Connect your Slack workspace to receive notifications about leads and engagement.</p>
            <Button
              className="mt-4 gap-2"
              onClick={() => {
                window.location.href = slackService.getInstallUrl(organizationId, user?.id);
              }}
            >
              <ExternalLink className="h-4 w-4" /> Connect to Slack
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {(config.slack_team_name || config.notification_channel_name) && (
              <div className="rounded-md border bg-muted/50 px-3 py-2 text-sm text-muted-foreground">
                {config.slack_team_name && <span>Team: <span className="font-medium text-foreground">{config.slack_team_name}</span></span>}
                {config.slack_team_name && config.notification_channel_name && <span className="mx-2">·</span>}
                {config.notification_channel_name && <span>Channel: <span className="font-medium text-foreground">#{config.notification_channel_name}</span></span>}
              </div>
            )}

            <div className="space-y-3">
              {NOTIFICATION_TOGGLES.map(({ key, label, description }) => (
                <div key={key} className="flex items-center justify-between gap-4">
                  <div className="space-y-0.5">
                    <Label htmlFor={`slack-${key}`} className="text-sm font-medium">{label}</Label>
                    <p className="text-xs text-muted-foreground">{description}</p>
                  </div>
                  <Switch
                    id={`slack-${key}`}
                    checked={!!config[key as keyof SlackConfig]}
                    onCheckedChange={() => handleToggle(key, !!config[key as keyof SlackConfig])}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        <DialogFooter className="flex-row justify-between sm:justify-between">
          {config?.connected && (
            <Button
              variant="ghost"
              size="sm"
              className="text-destructive hover:text-destructive hover:bg-destructive/10"
              onClick={async () => {
                if (!window.confirm('Are you sure you want to disconnect Slack? You will stop receiving notifications.')) return;
                const response = await slackService.disconnect(organizationId);
                if (response.success) {
                  queryClient.invalidateQueries({ queryKey: queryKeys.slack.config(organizationId) });
                  toast({ title: 'Slack disconnected', description: 'Your Slack workspace has been disconnected.' });
                  onOpenChange(false);
                } else {
                  toast({ title: 'Failed to disconnect', description: 'Please try again.', variant: 'destructive' });
                }
              }}
            >
              Disconnect
            </Button>
          )}
          <Button onClick={() => onOpenChange(false)}>Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SlackConfigDialog;
